import api from '../axiosConfig';

/**
 * Calculator API Service
 * Unified API layer for all calculator functionality
 */
const calculatorApi = {
  // ============================================================================
  // BUSINESS SETTINGS
  // ============================================================================
  
  /**
   * Get business settings for the current account
   */
  getBusinessSettings: async () => {
    try {
      const response = await api.get('/calculator/settings/business');
      return response.data;
    } catch (error) {
      console.error('Error fetching business settings:', error);
      throw error;
    }
  },
  
  /**
   * Update business settings
   * @param {Object} data - Settings to update
   */
  updateBusinessSettings: async (data) => {
    try {
      const response = await api.put('/calculator/settings/business', data);
      return response.data;
    } catch (error) {
      console.error('Error updating business settings:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // TRADING HOURS
  // ============================================================================
  
  /**
   * Get trading hours configuration
   */
  getTradingHours: async () => {
    try {
      const response = await api.get('/calculator/trading-hours');
      return response.data;
    } catch (error) {
      console.error('Error fetching trading hours:', error);
      throw error;
    }
  },
  
  /**
   * Update trading hours configuration
   * @param {Object} data - Trading hours data (monday_open, monday_start, monday_end, etc.)
   */
  updateTradingHours: async (data) => {
    try {
      const response = await api.put('/calculator/trading-hours', data);
      return response.data;
    } catch (error) {
      console.error('Error updating trading hours:', error);
      throw error;
    }
  },
  
  /**
   * Calculate total hours from trading hours data
   * @param {Object} data - Trading hours data
   */
  calculateTradingHours: async (data) => {
    try {
      const response = await api.post('/calculator/trading-hours/calculate', data);
      return response.data;
    } catch (error) {
      console.error('Error calculating trading hours:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // SERVICE CATEGORIES
  // ============================================================================
  
  /**
   * Get all service categories
   */
  getServiceCategories: async () => {
    try {
      const response = await api.get('/calculator/services/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching service categories:', error);
      throw error;
    }
  },
  
  /**
   * Create a new service category
   * @param {Object} data - Category data (name, description)
   */
  createServiceCategory: async (data) => {
    try {
      const response = await api.post('/calculator/services/categories', data);
      return response.data;
    } catch (error) {
      console.error('Error creating service category:', error);
      throw error;
    }
  },
  
  /**
   * Update a service category
   * @param {number} id - Category ID
   * @param {Object} data - Updated category data
   */
  updateServiceCategory: async (id, data) => {
    try {
      const response = await api.put(`/calculator/services/categories/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service category:', error);
      throw error;
    }
  },
  
  /**
   * Delete a service category
   * @param {number} id - Category ID
   */
  deleteServiceCategory: async (id) => {
    try {
      const response = await api.delete(`/calculator/services/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting service category:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // SERVICES
  // ============================================================================
  
  /**
   * Get all services
   * @param {number} categoryId - Optional: filter by category
   */
  getServices: async (categoryId = null) => {
    try {
      const params = categoryId ? { category_id: categoryId } : {};
      const response = await api.get('/calculator/services', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  },
  
  /**
   * Get a single service by ID
   * @param {number} id - Service ID
   */
  getService: async (id) => {
    try {
      const response = await api.get(`/calculator/services/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service:', error);
      throw error;
    }
  },
  
  /**
   * Create a new service
   * @param {Object} data - Service data
   */
  createService: async (data) => {
    try {
      const response = await api.post('/calculator/services', data);
      return response.data;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },
  
  /**
   * Update a service
   * @param {number} id - Service ID
   * @param {Object} data - Updated service data
   */
  updateService: async (id, data) => {
    try {
      const response = await api.put(`/calculator/services/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },
  
  /**
   * Delete a service
   * @param {number} id - Service ID
   */
  deleteService: async (id) => {
    try {
      const response = await api.delete(`/calculator/services/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  },
  
  /**
   * Save service (create or update based on ID presence)
   * @param {number|null} id - Service ID (null for new service)
   * @param {Object} data - Service data
   */
  saveService: async (id, data) => {
    if (id) {
      return calculatorApi.updateService(id, data);
    } else {
      return calculatorApi.createService(data);
    }
  },
  
  // ============================================================================
  // SERVICE COST ANALYSIS
  // ============================================================================
  
  /**
   * Get cost analysis for a specific service
   * @param {number} serviceId - Service ID
   */
  getServiceAnalysis: async (serviceId) => {
    try {
      const response = await api.get(`/calculator/services/${serviceId}/cost-analysis`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service analysis:', error);
      throw error;
    }
  },
  
  /**
   * Get cost analysis for all services
   */
  getServicesAnalysis: async () => {
    try {
      const response = await api.get('/calculator/services/cost-analysis/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching services analysis:', error);
      throw error;
    }
  },
  
  /**
   * Calculate service costs with specific parameters
   * @param {number} serviceId - Service ID
   * @param {Object} params - Calculation parameters (profit_margin, vat_rate, etc.)
   */
  calculateServiceCosts: async (serviceId, params = {}) => {
    try {
      const response = await api.post(`/calculator/services/${serviceId}/calculate`, params);
      return response.data;
    } catch (error) {
      console.error('Error calculating service costs:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // PRODUCTS
  // ============================================================================
  
  /**
   * Get all products (for dropdowns)
   */
  getProducts: async () => {
    try {
      const response = await api.get('/calculator/products');
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // SERVICE PRODUCTS
  // ============================================================================
  
  /**
   * Get products linked to a service
   * @param {number} serviceId - Service ID
   */
  getServiceProducts: async (serviceId) => {
    try {
      const response = await api.get(`/calculator/services/${serviceId}/products`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service products:', error);
      throw error;
    }
  },
  
  /**
   * Add a product to a service
   * @param {Object} data - { service_id, product_id, usage_amount, usage_unit }
   */
  addProductToService: async (data) => {
    try {
      const response = await api.post('/calculator/service-products', data);
      return response.data;
    } catch (error) {
      console.error('Error adding product to service:', error);
      throw error;
    }
  },
  
  /**
   * Update service product usage
   * @param {number} id - Service product junction ID
   * @param {Object} data - Updated data
   */
  updateServiceProduct: async (id, data) => {
    try {
      const response = await api.put(`/calculator/service-products/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating service product:', error);
      throw error;
    }
  },
  
  /**
   * Remove a product from a service
   * @param {number} id - Service product junction ID
   */
  deleteServiceProduct: async (id) => {
    try {
      const response = await api.delete(`/calculator/service-products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting service product:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // STAFF (Integration with existing Employee data)
  // ============================================================================
  
  /**
   * Get staff data for calculator
   * This pulls from the main Employee table
   */
  getStaff: async () => {
    try {
      const response = await api.get('/calculator/staff');
      return response.data;
    } catch (error) {
      console.error('Error fetching staff data:', error);
      throw error;
    }
  },
  
  /**
   * Update staff hourly rate
   * @param {number} id - Employee ID
   * @param {Object} data - { hourly_rate }
   */
  updateStaffRate: async (id, data) => {
    try {
      const response = await api.put(`/calculator/staff/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating staff rate:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // EXPENSES (Integration with existing Expenditure data)
  // ============================================================================
  
  /**
   * Get expenses for overhead calculation
   * This pulls from the main Expenditure table
   */
  getExpenses: async () => {
    try {
      const response = await api.get('/calculator/expenses');
      return response.data;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  },
  
  /**
   * Create a new expense
   * @param {Object} data - Expense data
   */
  createExpense: async (data) => {
    try {
      const response = await api.post('/calculator/expenses', data);
      return response.data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  },
  
  /**
   * Update an expense
   * @param {number} id - Expense ID
   * @param {Object} data - Updated expense data
   */
  updateExpense: async (id, data) => {
    try {
      const response = await api.put(`/calculator/expenses/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  },
  
  /**
   * Delete an expense
   * @param {number} id - Expense ID
   */
  deleteExpense: async (id) => {
    try {
      const response = await api.delete(`/calculator/expenses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  },
  
  /**
   * Get expense summary for a specific period
   * @param {string} period - 'current', 'previous', 'last3months', 'last6months', 'last12months'
   */
  getExpenseSummary: async (period = 'current') => {
    try {
      const response = await api.get(`/calculator/expenses/summary?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching expense summary:', error);
      throw error;
    }
  },
  
  /**
   * Get expense summary configuration
   */
  getExpenseSummaryConfig: async () => {
    try {
      const response = await api.get('/calculator/expenses/summary/config');
      return response.data;
    } catch (error) {
      console.error('Error fetching expense summary config:', error);
      throw error;
    }
  },
  
  /**
   * Update expense summary configuration
   * @param {Object} data - Configuration data
   */
  updateExpenseSummaryConfig: async (data) => {
    try {
      const response = await api.post('/calculator/expenses/summary/config', data);
      return response.data;
    } catch (error) {
      console.error('Error updating expense summary config:', error);
      throw error;
    }
  },
  
  /**
   * Recalculate overhead rates based on actual expenses
   * @param {Object} data - { period, excluded_categories, utilization_rate }
   */
  recalculateOverhead: async (data) => {
    try {
      const response = await api.post('/calculator/expenses/recalculate-overhead', data);
      return response.data;
    } catch (error) {
      console.error('Error recalculating overhead:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // PRICE UPDATES (Bulk pricing operations)
  // ============================================================================
  
  /**
   * Update multiple service prices at once
   * @param {Object} data - { services: [{id, new_price}], notes, update_date }
   */
  updateServicePrices: async (data) => {
    try {
      const response = await api.post('/calculator/services/update-prices', data);
      return response.data;
    } catch (error) {
      console.error('Error updating service prices:', error);
      throw error;
    }
  },
  
  /**
   * Get price update history
   */
  getPriceUpdateHistory: async () => {
    try {
      const response = await api.get('/calculator/services/price-update-history');
      return response.data;
    } catch (error) {
      console.error('Error fetching price update history:', error);
      throw error;
    }
  },
  
  /**
   * Get details for a specific price update
   * @param {number} updateId - Price update ID
   */
  getPriceUpdateDetails: async (updateId) => {
    try {
      const response = await api.get(`/calculator/services/price-update/${updateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching price update details:', error);
      throw error;
    }
  },
  
  // ============================================================================
  // IMPORT/EXPORT
  // ============================================================================
  
  /**
   * Import services from file
   * @param {FormData} formData - File upload form data
   */
  importServices: async (formData) => {
    try {
      const response = await api.post('/calculator/import-services', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error importing services:', error);
      throw error;
    }
  },
  
  /**
   * Export services to file
   */
  exportServices: async () => {
    try {
      const response = await api.get('/calculator/export-services', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting services:', error);
      throw error;
    }
  }
};

export default calculatorApi;